import React, { useId, useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Custom Tooltip Matching UI
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="rounded-[8px] border border-divider bg-background-surface px-2.5 py-2 shadow-[0_4px_4px_rgba(0,0,0,0.08)]">
        <div className="flex min-w-[150px] justify-between gap-6 text-[10px] font-medium leading-3 text-text-secondary">
          <span>{data.date}</span>
          <span>{data.time}</span>
        </div>
        <p className="mt-1 text-[10px] leading-3 text-text-secondary">
          Price:{" "}
          <span className="font-semibold text-text-primary">
            $
            {data.price?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </p>
        <p className="text-[10px] leading-3 text-text-secondary">
          Vol 24h:{" "}
          <span className="font-semibold text-text-primary">
            ${data.volume?.toLocaleString()}
          </span>
        </p>
      </div>
    );
  }
  return null;
};

// Formatter for Y-Axis labels (e.g., $80K, $70K, $1.9K)
const formatYAxis = (value) => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(2)}`;
};

const AssetDetailChart = ({
  sparkline = [],
  volumeUsd = 0,
  timeRange = "7D",
}) => {
  const rawGradientId = useId();
  const gradientId = `sparklineGradient-${rawGradientId.replace(/:/g, "")}`;
    
  // 1. Process sparkline data based on timeRange
  const { chartData, yDomain, xTicks } = useMemo(() => {
    const points = Array.isArray(sparkline) ? sparkline : [];

    if (!points.length) {
      return {
        chartData: [],
        yDomain: [0, 1],
        xTicks: [],
      };
    }

    let slicedPoints = points;

    switch (timeRange) {
      case "24H":
      case "1D":
        slicedPoints = points.slice(-24);
        break;

      case "7D":
      case "1W":
        slicedPoints = points.slice(-7);
        break;

      case "1M":
        slicedPoints = points.slice(-30);
        break;

      case "3M":
        slicedPoints = points.slice(-90);
        break;

      case "1Y":
        slicedPoints = points.slice(-365);
        break;

      case "MAX":
      default:
        break;
    }

    const prices = slicedPoints.map(([, price]) => price);

    const min = Math.min(...prices);
    const max = Math.max(...prices);

    const padding = (max - min) * 0.05 || max * 0.01;

    const avgVol = (volumeUsd || 100000) / slicedPoints.length;

    const formattedData = slicedPoints.map(([timestamp, price], idx) => {
      const pointDate = new Date(timestamp);

      const prevPrice = idx > 0 ? slicedPoints[idx - 1][1] : price;

      const volatility = Math.abs((price - prevPrice) / prevPrice);

      let label = "";

      switch (timeRange) {
        case "24H":
        case "1D":
          label = pointDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            hour12: true,
          });
          break;

        case "7D":
        case "1W":
          label = pointDate.toLocaleDateString("en-US", {
            weekday: "short",
          });
          break;

        case "1M":
        case "3M":
        case "YTD":
          label = pointDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
          break;

        case "1Y":
          label = pointDate.toLocaleDateString("en-US", {
            month: "short",
          });
          break;

        case "MAX":
          label = pointDate.getFullYear().toString();
          break;

        default:
          label = pointDate.toLocaleDateString();
      }

      return {
        index: idx,
        timestamp,
        price,
        volume: Math.round(
          avgVol * (0.5 + volatility * 50 + (idx % 3) * 0.2)
        ),
        label,
        date: pointDate.toLocaleDateString("en-US"),
        time: pointDate.toLocaleTimeString("en-US"),
      };
    });

    const ticks = formattedData.map((item) => item.index);

    return {
      chartData: formattedData,
      yDomain: [min - padding, max + padding],
      xTicks: ticks,
    };
  }, [sparkline, volumeUsd, timeRange]);

   if (!chartData.length) {
    return (
      <div className="w-full">
        <div className="flex h-[215px] w-full flex-col items-center justify-center rounded-[8px] border border-dashed border-divider bg-background-surface px-6 text-center">
          {/* Icon */}
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-background-secondary">
            <svg
              className="h-6 w-6 text-text-secondary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 3v18h18" />
              <path d="M7 15l3-3 3 2 5-6" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-sm font-semibold text-text-primary">
            Chart unavailable
          </h3>

          {/* Description */}
          <p className="mt-1 max-w-xs text-xs leading-5 text-text-secondary">
            We couldn't find enough historical market data to display this chart.
            Try selecting another time range or check back later.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full">
      <div className="relative h-[215px] w-full rounded-[8px] bg-background-surface p-2 sm:border sm:border-divider">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 12, right: 12, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4AB932" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#4AB932" stopOpacity={0.0} />
              </linearGradient>
            </defs>

            {/* Grid Lines */}
            <CartesianGrid
              vertical={false}
              stroke="#f0f0f0"
              strokeDasharray="0"
            />

            {/* X-Axis (Filtered Unique Labels) */}
            <XAxis
              dataKey="index"
              ticks={xTicks}
              axisLine={false}
              tickLine={false}
              tickFormatter={(idx) => chartData[idx]?.label || ""}
              tick={{ fill: "#8E8E93", fontSize: 10, fontWeight: 500 }}
              dy={5}
            />

            {/* Price Y-Axis (Right Aligned) */}
            <YAxis
              yAxisId="priceAxis"
              orientation="right"
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatYAxis}
              tick={{ fill: "#8E8E93", fontSize: 10, fontWeight: 500 }}
              width={44}
            />

            {/* Volume Y-Axis (Hidden Axis for proportional bars) */}
            <YAxis
              yAxisId="volumeAxis"
              orientation="left"
              domain={[0, (dataMax) => dataMax * 4]}
              hide
            />

            {/* Crosshair & Tooltip */}
            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "#dbdee4",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
            />

            {/* Volume Bars */}
            <Bar
              yAxisId="volumeAxis"
              dataKey="volume"
              fill="#D0D0D0"
              barSize={2}
              radius={[0.5, 0.5, 0, 0]}
            />

            {/* Area Line Chart for Sparkline */}
            <Area
              yAxisId="priceAxis"
              type="monotone"
              dataKey="price"
              stroke="#4AB932"
              strokeWidth={1.5}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AssetDetailChart;